import { useCallback, useEffect, useState } from 'react';
import { DashboardActionAlert } from '@/componentsv2/dashboard/DashboardActionAlert';
import { useOrderIssuesTotal } from '@/contexts/OrderIssuesTotalContext';
import { useInterval } from '@/hooks/useInterval';
import { useEndpoints } from '@/session/useEndpoints';

const REFRESH_MS = 60 * 1000;

// A string, not JSX text: the compiler drops the space after </strong> from multi-line text containing &apos;.
const MESSAGE =
  "Some orders have problems with your sales channels or fulfillment providers, such as orders that failed to import or fulfillments that couldn't be sent. Review them to avoid shipping delays or marketplace penalties.";

/**
 * Dashboard banner for the organization's open order issues; nothing while there are none or the first read fails.
 * Entering the dashboard reads a fresh total. It then refreshes every minute and when the tab becomes visible again,
 * until dismissed or a read fails.
 */
export const OrderIssuesAlert = () => {
  const { organization } = useEndpoints();
  const { total, failed, refresh } = useOrderIssuesTotal();
  const [dismissed, setDismissed] = useState(false);
  const isRefreshing = !dismissed && !failed;

  const refreshIfVisible = useCallback(() => {
    if (!document.hidden) refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useInterval(refreshIfVisible, isRefreshing ? REFRESH_MS : null);

  useEffect(() => {
    if (!isRefreshing) return;
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => document.removeEventListener('visibilitychange', refreshIfVisible);
  }, [isRefreshing, refreshIfVisible]);

  if (total === 0 || dismissed) return null;

  return (
    <DashboardActionAlert actionHref={`/${organization}/order-issues/`} actionLabel='View Order Issues' onClose={() => setDismissed(true)}>
      <strong>Order Issues:</strong> {MESSAGE}
    </DashboardActionAlert>
  );
};
